<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import CannedAudioPicker from './CannedAudioPicker.vue';
import CannedPhotoPicker from './CannedPhotoPicker.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    CannedAudioPicker,
    CannedPhotoPicker,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    existingAudioUrl: { type: String, default: '' },
    existingAudioFilename: { type: String, default: '' },
    existingPhotos: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      audioFile: null,
      photoFiles: [],
      currentAudioUrl: this.existingAudioUrl,
      currentAudioFilename: this.existingAudioFilename,
      currentPhotos: [...this.existingPhotos],
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {},
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    hasAudio() {
      return this.audioFile instanceof File || !!this.currentAudioUrl;
    },
    hasPhotos() {
      return this.photoFiles.length > 0 || this.currentPhotos.length > 0;
    },
    canSubmit() {
      return (
        !this.v$.shortCode.$invalid &&
        (this.content?.trim() || this.hasAudio || this.hasPhotos)
      );
    },
  },
  methods: {
    setPageName({ name }) {
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.audioFile = null;
      this.photoFiles = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    async removeExistingAudio() {
      try {
        await this.$store.dispatch('removeCannedAudio', this.id);
        this.currentAudioUrl = '';
        this.currentAudioFilename = '';
        useAlert(this.$t('CANNED_MGMT.AUDIO.REMOVE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('CANNED_MGMT.AUDIO.REMOVE_ERROR'));
      }
    },
    async removeExistingPhoto(photoId) {
      try {
        await this.$store.dispatch('removeCannedPhoto', {
          id: this.id,
          photoId,
        });
        this.currentPhotos = this.currentPhotos.filter(p => p.id !== photoId);
        useAlert(this.$t('CANNED_MGMT.PHOTOS.REMOVE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('CANNED_MGMT.PHOTOS.REMOVE_ERROR'));
      }
    },
    editCannedResponse() {
      this.editCanned.showLoading = true;
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content || '',
          audio_file: this.audioFile,
          photos: this.photoFiles,
        })
        .then(() => {
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
            <span class="text-xs font-normal text-n-slate-11">
              {{ $t('CANNED_MGMT.AUDIO.CONTENT_OPTIONAL_HINT') }}
            </span>
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
            />
          </div>
        </div>

        <div class="w-full mt-2">
          <label>
            {{ $t('CANNED_MGMT.AUDIO.LABEL') }}
            <span class="text-xs font-normal text-n-slate-11">
              {{ $t('CANNED_MGMT.AUDIO.HINT') }}
            </span>
          </label>
          <CannedAudioPicker
            v-model="audioFile"
            :existing-audio-url="currentAudioUrl"
            :existing-audio-filename="currentAudioFilename"
            @remove-existing="removeExistingAudio"
          />
        </div>

        <div class="w-full mt-2">
          <label>
            {{ $t('CANNED_MGMT.PHOTOS.LABEL') }}
            <span class="text-xs font-normal text-n-slate-11">
              {{ $t('CANNED_MGMT.PHOTOS.HINT') }}
            </span>
          </label>
          <CannedPhotoPicker
            v-model="photoFiles"
            :existing-photos="currentPhotos"
            @remove-existing="removeExistingPhoto"
          />
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="!canSubmit || editCanned.showLoading"
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
